<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { SchoolClassType } from '@/api/generated/api.enum';
import { useListedStudentsIntro, type IListedStudents } from '@/composables/student/useListedStudents';
import StudentListItem from '@/core/list-items/StudentListItem.vue';
import StudentSearchSelect from '@/core/selects/StudentSearchSelect.vue';
import StudentDraftList from '@/core/students/StudentDraftList.vue';
import { IOnboardingStudent } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import type { TViewedSchoolClass } from '@/schoolClasses/useGroup';
import { mergeByKey } from 'common/utils/array.utils';
import { getClassName } from 'common/utils/format.utils';
import { computed, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

type TSchoolClassForm = Components.Schemas.CreateSchoolClassDto | Components.Schemas.SchoolClassDto | null;

export interface IStudentFormSelection {
  students: IOnboardingStudent[];
  selectedStudents: Components.Schemas.StudentDto[] | null;
}

const emit = defineEmits<{
  continue: [value: IStudentFormSelection];
  'update:isValid': [value: boolean];
  'update:students': [value: IStudentFormSelection];
}>();

const props = defineProps<{
  disabled?: boolean;
  schoolClass: TSchoolClassForm;
  alreadyAddedStudents: IOnboardingStudent[];
  alreadySelectedStudents: Components.Schemas.StudentDto[];
  selectableStudents: Components.Schemas.StudentDto[] | null;
}>();
const { alreadyAddedStudents, alreadySelectedStudents, schoolClass, selectableStudents } = toRefs(props);
const { t } = useI18n();
const { isGroup, tContainer } = useContainerTranslations();

// the container the students go into: a class draft arrives as a prop, a group draft only through its provided type
const documentationContext = computed<TSchoolClassForm | TViewedSchoolClass>(
  () => schoolClass.value ?? (isGroup.value ? { type: SchoolClassType.group } : null),
);

const studentDraftListRef = useTemplateRef('studentDraftListRef');
const createdStudents = ref<IOnboardingStudent[]>([...alreadyAddedStudents.value]);
const selectedStudents = ref<Components.Schemas.StudentDto[]>([...alreadySelectedStudents.value]);
const listedStudents = ref<IListedStudents | null>(null);
const listIntro = useListedStudentsIntro(listedStudents, (count) => tContainer('alreadyInContainerCount', { count }));
const selectableStudentsInternal = computed(() =>
  (selectableStudents.value ?? []).filter(
    (student) => !selectedStudents.value.some((selected) => selected.id === student.id),
  ),
);
const description = computed(() => {
  if (listIntro.value) return listIntro.value;
  if (!documentationContext.value) return null;
  return schoolClass.value
    ? tContainer('studentsDescription', { className: getClassName(schoolClass.value) })
    : t('groups.studentsStepDescription');
});

const onSubmit = (): void => {
  if (!studentDraftListRef.value?.validate()) return;

  emit('continue', {
    students: createdStudents.value,
    selectedStudents: selectedStudents.value,
  });
};

const handleStudentSelected = (student: Components.Schemas.StudentDto | null | 'all'): void => {
  if (!student || student === 'all') return;
  selectedStudents.value = [...selectedStudents.value, student];
};

const handleRemoveSelectedStudent = (student: Components.Schemas.StudentDto): void => {
  selectedStudents.value = selectedStudents.value.filter((s) => s.id !== student.id);
};

const initEmptyStudents = async (): Promise<void> => {
  await studentDraftListRef.value?.addRow();
};

/** The new names of an uploaded list become rows, and the school's students it names are taken in. */
const addListedStudents = (list: IListedStudents): void => {
  listedStudents.value = list;
  studentDraftListRef.value?.addStudents(list.newNames);
  selectedStudents.value = mergeByKey(selectedStudents.value, list.schoolStudents, ({ id }) => id);
};

watch([createdStudents, selectedStudents], () => {
  emit('update:students', {
    students: createdStudents.value,
    selectedStudents: selectedStudents.value,
  });
});

const reset = (): void => {
  studentDraftListRef.value?.reset();
  selectedStudents.value = [];
  listedStudents.value = null;
};

defineExpose({
  onSubmit,
  reset,
  initEmptyStudents,
  addListedStudents,
});
</script>

<template>
  <div
    :inert="props.disabled"
    class="grid h-full w-full min-w-0 grid-rows-[auto_auto_1fr] overflow-x-hidden px-lg md:px-content"
  >
    <p v-if="description" class="text-subtitle mb-md min-w-0 [overflow-wrap:anywhere] hyphens-auto">
      {{ description }}
    </p>
    <!-- an uploaded list names the students itself -->
    <StudentSearchSelect
      v-if="!listedStudents"
      class="mb-lg min-w-0 max-w-full"
      :students="selectableStudentsInternal"
      :placeholder="$t('classes.studentsPlaceholder')"
      :label="$t('classes.addStudentsLabel')"
      @student-selected="handleStudentSelected"
    />
    <StudentDraftList
      ref="studentDraftListRef"
      :initial-students="alreadyAddedStudents"
      :empty-add-label="$t('onboarding.students.addStudentOptional')"
      @update:students="createdStudents = $event"
      @update:is-valid="emit('update:isValid', $event)"
    >
      <StudentListItem
        v-for="student in selectedStudents"
        :key="student.id"
        :student="student"
        type="remove"
        @click:remove="handleRemoveSelectedStudent(student)"
      />
    </StudentDraftList>
  </div>
</template>
